import { faHome, faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "../Link";
import { cn } from "~/lib/utils";
import { useEffect, useState } from "react";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";

// Scroll distance (px) past which the page header is considered hidden
// behind the sticky breadcrumb, revealing the compact icon + name.
const STICKY_SCROLL_OFFSET = 120;

// // // //

/**
 * LastBreadcrumb
 * Renders the last breadcrumb item in the breadcrumbs list with an animated icon
 */
function LastBreadcrumb({
    name,
    iconUrl,
}: {
    name: string;
    iconUrl: string | null;
}) {
    const [isSticky, setIsSticky] = useState(false);

    useEffect(() => {
        let frame = 0;
        const update = () => {
            frame = 0;
            setIsSticky(window.scrollY >= STICKY_SCROLL_OFFSET);
        };
        const scheduleUpdate = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };
        window.addEventListener("scroll", scheduleUpdate, { passive: true });
        scheduleUpdate();

        return () => {
            cancelAnimationFrame(frame);
            window.removeEventListener("scroll", scheduleUpdate);
        };
    }, []);

    return (
        <span className="inline-flex h-6 max-w-full items-center align-middle">
            <span
                aria-hidden="true"
                className={cn(
                    "relative h-6 shrink-0 overflow-hidden transition-[width] duration-300 ease-out motion-reduce:transition-none",
                    { "w-7": isSticky, "w-0": !isSticky },
                )}
            >
                <span
                    className={cn(
                        "flex h-6 w-5 items-center transition-[translate,opacity] duration-300 ease-out motion-reduce:transition-none",
                        {
                            "translate-y-0 opacity-100": isSticky,
                            "translate-y-full opacity-0": !isSticky,
                        },
                    )}
                >
                    <ImageLoader
                        src={iconUrl}
                        fallbackSrc={RWA_COIN_ICON}
                        alt={`${name} Logo`}
                        className="size-5 shrink-0 rounded-full object-contain"
                        fallbackClassName="size-5 shrink-0 rounded-full object-contain bg-white p-0.5"
                    />
                </span>
            </span>
            <span
                className={cn(
                    "min-w-0 truncate transition-colors duration-300 motion-reduce:transition-none",
                    { "text-foreground": isSticky },
                )}
            >
                {name}
            </span>
        </span>
    );
}

// // // //

export interface BreadcrumbItem {
    name: string;
    icon_url?: string | null;
    href?: string;
    current?: boolean;
}

interface Props {
    pages: Array<BreadcrumbItem>;
    // className?: string;
}

export function Breadcrumbs(props: Props) {
    const { pages } = props;
    const [isOpen, setIsOpen] = useState(false);

    if (pages.length === 0) {
        return null;
    }

    const lastPage = pages[pages.length - 1];
    const otherPages = pages.slice(0, -1).reverse();

    return (
        <nav
            className="flex border-b bg-sidebar sticky top-nav-height z-240 select-none max-md:top-(--app-mobile-nav-height)"
            aria-label="Breadcrumb"
        >
            {isOpen && (
                <div className="md:hidden absolute top-full mt-0 w-full bg-sidebar border-y shadow-lg z-50">
                    <ul className="py-1">
                        {otherPages.map((page) => (
                            <li key={page.name}>
                                {page.href && (
                                    <Link
                                        href={page.href}
                                        analyticsLabel={`Breadcrumb - ${page.name}`}
                                        className="block px-4 py-2 text-sm text-primary hover:bg-slate-50"
                                    >
                                        {page.name}
                                    </Link>
                                )}
                            </li>
                        ))}
                        {/* Home item in dropdown */}
                        <li>
                            <Link
                                href="/"
                                analyticsLabel="Breadcrumb - Home"
                                className="flex items-center px-4 py-2 text-sm text-primary hover:bg-slate-50"
                            >
                                <FontAwesomeIcon
                                    icon={faHome}
                                    className="h-4 w-4 mr-2"
                                    aria-hidden="true"
                                />
                                Home
                            </Link>
                        </li>
                    </ul>
                </div>
            )}

            <ol
                role="list"
                className="flex w-full max-w-(--breakpoint-xl) md:space-x-4 px-4 sm:px-6 lg:px-8"
            >
                {/* Home button - visible only on desktop */}
                <li className="hidden md:flex">
                    <div className="flex items-center">
                        <Link
                            analyticsLabel="Breadcrumbs - Home"
                            href="/"
                            className="text-primary hover:text-blue-700"
                        >
                            <FontAwesomeIcon
                                icon={faHome}
                                className="h-5 w-5 shrink-0"
                                aria-hidden="true"
                            />
                            <span className="sr-only">Home</span>
                        </Link>
                    </div>
                </li>

                {/* Mobile dropdown */}
                <li className="md:hidden relative flex items-center">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="p-1 rounded-sm hover:bg-slate-100 transition-colors duration-200 flex items-center"
                        aria-label="Show full breadcrumb"
                    >
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className="h-4 w-4 text-slate-500"
                        />
                    </button>
                </li>

                {/* Desktop breadcrumbs */}
                {otherPages.length > 0 && (
                    <li className="hidden md:flex items-center space-x-4">
                        {otherPages.reverse().map((page) => (
                            <div key={page.name} className="flex items-center">
                                <svg
                                    className="h-full w-6 shrink-0 text-border"
                                    viewBox="0 0 24 44"
                                    preserveAspectRatio="none"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                                </svg>
                                {page.href && (
                                    <Link
                                        href={page.href}
                                        analyticsLabel={`Breadcrumb - ${page.name}`}
                                        className="ml-4 text-sm text-primary hover:text-blue-700"
                                    >
                                        {page.name}
                                    </Link>
                                )}
                                {!page.href && (
                                    <span className="ml-4 text-sm text-muted-foreground">
                                        {page.name}
                                    </span>
                                )}
                            </div>
                        ))}
                    </li>
                )}

                {/* Last item (always visible) */}
                <li className="flex items-center">
                    <svg
                        className="h-full w-6 shrink-0 text-border"
                        viewBox="0 0 24 44"
                        preserveAspectRatio="none"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                    </svg>
                    <span
                        className="ml-4 text-sm text-muted-foreground truncate max-w-[200px] lg:max-w-none"
                        aria-current={lastPage.current ? "page" : undefined}
                    >
                        {/* Just display name if no icon is provided */}
                        {lastPage.icon_url === undefined && (
                            <>{lastPage.name}</>
                        )}

                        {/* Display name and icon if provided */}
                        {lastPage.icon_url !== undefined && (
                            <LastBreadcrumb
                                name={lastPage.name}
                                iconUrl={lastPage.icon_url}
                            />
                        )}
                    </span>
                </li>
            </ol>
        </nav>
    );
}
